import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CalendarRange, Camera, Cloud, Film, LayoutDashboard, Lock, LogOut, Mail,
  Menu, Radio, Settings, ShieldCheck, Tag, Upload, Users, Video, X, Archive,
} from "lucide-react";

const menuItems = [
  { icon: LayoutDashboard, label: "Tablou de bord", path: "/" },
  { icon: CalendarRange, label: "Proiecte (șantiere)", path: "/proiecte" },
  { icon: Camera, label: "Camere", path: "/camere" },
  { icon: Upload, label: "Încărcare arhive", path: "/upload" },
  { icon: Film, label: "Filme zilnice", path: "/filme" },
  { icon: Tag, label: "Evenimente", path: "/evenimente" },
  { icon: Radio, label: "Live View", path: "/live" },
  { icon: Users, label: "Utilizatori", path: "/utilizatori", adminOnly: true },
  { icon: ShieldCheck, label: "Membri șantiere", path: "/membri", adminOnly: true },
  { icon: Settings, label: "Setări & Gateway", path: "/setari", adminOnly: true },
  { icon: Cloud, label: "Cloud Sync (Reolink)", path: "/cloud-sync", adminOnly: true },
  { icon: Archive, label: "Arhivă NAS", path: "/arhiva-nas", adminOnly: true },
];

function Brand({ compact }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center shrink-0 shadow-sm">
        <Video className="h-5 w-5 text-primary-foreground" />
      </div>
      {!compact && (
        <div className="flex flex-col leading-tight">
          <span className="font-bold tracking-tight text-sm text-white">HGS Video</span>
          <span className="text-[10px] uppercase tracking-widest text-white/50">Highberg Solution</span>
        </div>
      )}
    </div>
  );
}

function LoginScreen() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [pending, setPending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) { setError("Completați email-ul și parola"); return; }
    setPending(true);
    try {
      await login(email.toLowerCase().trim(), password);
    } catch (err) {
      setError(apiError(err) || "Email sau parolă incorectă");
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[hsl(200_28%_12%)] via-[hsl(200_30%_16%)] to-[hsl(188_46%_22%)] p-4">
      <div className="w-full max-w-md bg-card rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-6" data-testid="login-card">
        <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center shadow-lg">
          <Video className="h-7 w-7 text-primary-foreground" />
        </div>
        <div className="flex flex-col items-center gap-1.5 text-center">
          <h1 className="text-2xl font-bold tracking-tight">HGS Video</h1>
          <p className="text-sm text-muted-foreground max-w-xs">
            Monitorizare video șantiere — Highberg Solution. Autentificați-vă cu email și parolă.
          </p>
        </div>
        <form onSubmit={submit} className="w-full flex flex-col gap-3">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
              className="pl-9" autoComplete="email" data-testid="login-email" />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input type="password" placeholder="Parolă" value={password} onChange={(e) => setPassword(e.target.value)}
              className="pl-9" autoComplete="current-password" data-testid="login-password" />
          </div>
          {error && <p className="text-sm text-destructive text-center" data-testid="login-error">{error}</p>}
          <Button type="submit" size="lg" className="w-full mt-1" disabled={pending} data-testid="login-submit">
            {pending ? "Se verifică..." : "Intră în cont"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export default function DashboardLayout({ children }) {
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Se încarcă...</div>;
  }
  if (!user) return <LoginScreen />;

  const isAdmin = user.role === "admin";
  const visible = menuItems.filter((m) => !m.adminOnly || isAdmin);
  const active = visible.find((i) => (i.path === "/" ? location.pathname === "/" : location.pathname.startsWith(i.path)));

  const Nav = () => (
    <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-0.5">
      {visible.map((item) => {
        const isActive = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
        const Icon = item.icon;
        return (
          <button
            key={item.path}
            data-testid={`nav-${item.path === "/" ? "dashboard" : item.path.slice(1)}`}
            onClick={() => { navigate(item.path); setMobileOpen(false); }}
            className={`w-full flex items-center gap-3 rounded-lg px-3 h-10 text-sm transition-colors text-left ${
              isActive
                ? "bg-primary text-primary-foreground font-medium shadow-sm"
                : "text-white/70 hover:bg-white/10 hover:text-white"
            }`}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="truncate">{item.label}</span>
          </button>
        );
      })}
    </nav>
  );

  const SidebarInner = () => (
    <div className="flex flex-col h-full bg-[hsl(var(--sidebar))]">
      <div className="h-16 flex items-center px-4 border-b border-white/10">
        <Brand />
      </div>
      <Nav />
      <div className="p-3 border-t border-white/10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="w-full flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-white/10 transition-colors text-left" data-testid="user-menu">
              <div className="h-9 w-9 rounded-full bg-primary/80 flex items-center justify-center text-white text-sm font-semibold shrink-0">
                {(user.name || user.email || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate leading-none">{user.name || "-"}</p>
                <p className="text-xs text-white/50 truncate mt-1">{isAdmin ? "Administrator" : "Operator"}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={logout} className="text-destructive focus:text-destructive cursor-pointer" data-testid="logout-btn">
              <LogOut className="mr-2 h-4 w-4" /> Deconectare
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0 h-screen sticky top-0">
        <SidebarInner />
      </aside>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative w-64 h-full">
            <SidebarInner />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden h-14 border-b flex items-center justify-between px-3 sticky top-0 bg-background/95 backdrop-blur z-40">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-toggle">
              <Menu className="h-5 w-5" />
            </Button>
            <span className="font-medium">{active?.label ?? "HGS Video"}</span>
          </div>
          <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center">
            <Video className="h-4 w-4 text-primary-foreground" />
          </div>
        </header>
        <main className="flex-1 p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
